import { useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Volume2, VolumeX } from 'lucide-react'
import { formatCardDateTime } from '@/lib/utils'
import { criticalReasonText, type Translate } from '@/lib/criticalEvents'
import { eventOpenTarget } from '@/lib/navigation'
import { unlockAlertSound } from '@/lib/alertSound'
import { useElementBottomVar } from '@/hooks/useElementHeightVar'
import { useCriticalAlertsContext, useCriticalQuietLeft } from './CriticalAlertsContext'

/** "1:59" */
function minutesSeconds(totalSeconds: number): string {
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`
}

/** A camera name longer than this is shortened with "…" in the bar (the full text is on hover). */
const NAME_MAX = 32
const shorten = (s: string) => (s.length > NAME_MAX ? `${s.slice(0, NAME_MAX - 1).trimEnd()}…` : s)

/**
 * #294 redesign (CP 294 Section 1.2) — the slim alert bar under the header on every page while any critical
 * alert is open (replaces the 294 core banner). "N critical alert(s) open — newest: <camera> · Spray sound ·
 * <time>" (on phones the short form "· <camera> · <time>"; the bar wraps), View (opens the newest, as a
 * toast click does), Acknowledge all, and the speaker button: "Quiet 2 min" (every tab of this browser),
 * then the time left; while the browser blocks sound it reads "Click to enable sound" and its click unlocks
 * the sound, after which the controller plays the critical sound. The speaker button is NOT the header's
 * 295 sound button (no data-alert-sound-button): that attribute would make the unlock play the chime too.
 * Only the text is the live region (the countdown would make a screen reader re-read it every second).
 * The bar's bottom edge is published as --crit-bar-bottom (the popups and the toasts start below it, also when
 * the superuser's environment banner sits above the header).
 */
export function CriticalAlertBar() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()
  const { open, total, quiet, soundBlocked, acknowledgeAll, acknowledgingAll } = useCriticalAlertsContext()
  const quietLeft = useCriticalQuietLeft()
  const ref = useRef<HTMLDivElement>(null)
  const shown = total > 0
  useElementBottomVar(ref, '--crit-bar-bottom', shown)
  if (!shown) return null

  const newest = open[0]
  const reasonText = newest ? criticalReasonText(newest, t as unknown as Translate) : ''
  // a WebSocket placeholder has no real time yet (its occurred_at is this browser's "now"): no time until the
  // real event arrives, so the time does not jump and the live text is not re-announced for it
  const time = newest && !newest._seeded ? formatCardDateTime(newest.occurred_at) : ''
  const camera = newest?.camera_name ?? ''
  const mode = soundBlocked ? 'enable' : quietLeft > 0 ? 'quiet' : 'normal'

  const onSpeaker = () => {
    if (mode === 'enable') void unlockAlertSound()
    else quiet()
  }
  const onView = () => {
    if (!newest) return
    const { to, options } = eventOpenTarget(location, newest.id)
    navigate(to, options)
  }

  return (
    <div ref={ref} data-testid="critical-bar" className="crit-bar">
      <div className="crit-bar-msg">
        <AlertTriangle className="crit-bar-icon" strokeWidth={2.2} aria-hidden />
        <div role="alert" className="crit-bar-text" title={newest ? [camera, reasonText, time].filter(Boolean).join(' · ') : undefined}>
          <strong>{t('critical.bar.count', { count: total })}</strong>
          {newest && (
            <>
              <span className="crit-bar-long"> {time
                ? t('critical.bar.newest', { camera: shorten(camera), reason: reasonText, time })
                : t('critical.bar.newestNoTime', { camera: shorten(camera), reason: reasonText })}</span>
              {/* phones: the short form (one of the two is hidden by CSS, so a screen reader reads one) */}
              <span className="crit-bar-short"> · {shorten(camera)}{time ? ` · ${time}` : ''}</span>
            </>
          )}
        </div>
      </div>
      <div className="crit-bar-actions">
        <button type="button" className="crit-bar-btn" onClick={onView} disabled={!newest}>
          {t('critical.bar.view')}
        </button>
        <button
          type="button"
          className="crit-bar-btn crit-bar-btn-solid"
          onClick={acknowledgeAll}
          disabled={acknowledgingAll || open.length === 0}
        >
          {t('critical.bar.acknowledgeAll')}
        </button>
        <button type="button" className="crit-bar-btn" data-mode={mode} onClick={onSpeaker}>
          {mode === 'normal'
            ? <Volume2 className="h-[15px] w-[15px]" aria-hidden />
            : <VolumeX className="h-[15px] w-[15px]" aria-hidden />}
          <span>
            {mode === 'enable'
              ? t('critical.bar.enableSound')
              : mode === 'quiet'
                ? t('critical.bar.quieted', { time: minutesSeconds(quietLeft) })
                : t('critical.bar.quiet')}
          </span>
        </button>
      </div>
    </div>
  )
}
